import { Route, Switch } from 'react-router-dom';
import { routerActions } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import React from 'react';
import { DATA_TYPES } from 'platform/common/dataType';
import { nonNil, TableCell } from 'platform/common/common.type';
import { Website } from 'platform/adminPanel/websites/website.type';
import { fetchAdBoardTemplates } from 'platform/adminPanel/masterTemplates/masterTemplates.service';
import { fetchWebsites } from 'platform/adminPanel/websites/website.service';
import { includesIgnoreCase } from 'platform/common/utils/filter.util';
import { usePromise } from 'platform/common/hooks/usePromise';
import FormattedTable from 'platform/common/components/FormattedTable/FormattedTable';
import InlineDropdown from 'platform/common/components/InlineDropdown/InlineDropdown';
import ListToolbar from 'platform/common/components/ListToolbar/ListToolbar';
import UpdatedOn from 'platform/common/components/UpdatedOn/UpdatedOn';
import { AdBoardTemplate, TeaserTemplate } from 'platform/adminPanel/masterTemplates/masterTemplates.type';
import {
    AdBoardTemplateCreate,
    AdBoardTemplateEdit,
} from 'platform/adminPanel/masterTemplates/AdBoardTemplate/AdBoardTemplateForm';

type AdBoardTemplateWithWebsite = AdBoardTemplate & {
    website: Website;
};

const fetchTeaserTemplatesWithWebsites = async (): Promise<AdBoardTemplateWithWebsite[]> => {
    const [templates, websites] = await Promise.all([fetchAdBoardTemplates(), fetchWebsites()]);
    return templates.map(t => ({
        ...t,
        website: nonNil(websites.find(w => t.websiteId === w.id)),
    }));
};

const AdBoardTemplateList = () => {
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = React.useState('');

    const [{ data, loading }, refetch] = usePromise([], fetchTeaserTemplatesWithWebsites, []);

    const adBoardTemplates = data.filter(t => includesIgnoreCase(t.name, searchQuery));

    const columnDefinitions = [
        {
            Header: 'ID',
            accessor: (t: AdBoardTemplateWithWebsite) => t.id,
            type: DATA_TYPES.ID,
            width: 80,
        },
        {
            Header: 'Name',
            accessor: (t: AdBoardTemplateWithWebsite) => t.name,
            type: DATA_TYPES.TEXT,
            minWidth: 200,
        },
        {
            Header: 'Website',
            accessor: (t: AdBoardTemplateWithWebsite) => t.website.name,
            type: DATA_TYPES.TEXT,
            minWidth: 250,
        },
        {
            Header: 'Edited',
            accessor: 'lastModifiedOn',
            width: 160,
            Cell: (cell: TableCell<AdBoardTemplateWithWebsite>) => (
                <UpdatedOn date={cell.original.lastModifiedOn} updatedBy={cell.original.lastModifiedBy} />
            ),
        },
        {
            className: () => 'edit-dropdown pull-right cell-align-right',
            width: 50,
            sortable: false,
            Cell: (cell: TableCell<AdBoardTemplateWithWebsite>) => (
                <InlineDropdown
                    items={[
                        {
                            label: 'Edit',
                            action: () =>
                                dispatch(
                                    routerActions.push(
                                        `/admin-panel/master-templates/ad-board/edit/${cell.original.id}`
                                    )
                                ),
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <div>
            <div className="mb-4">
                <ListToolbar
                    value={searchQuery}
                    onCreate={() => {
                        dispatch(routerActions.push('/admin-panel/master-templates/ad-board/new'));
                    }}
                    onSearch={setSearchQuery}
                />
            </div>
            <FormattedTable
                className="-striped"
                columns={columnDefinitions}
                data={adBoardTemplates}
                loading={loading}
            />
            <Switch>
                <Route
                    path={'/admin-panel/master-templates/ad-board/new'}
                    render={props => (
                        <AdBoardTemplateCreate
                            {...props}
                            redirectTo={'/admin-panel/master-templates/ad-board'}
                            canEdit
                            afterSubmit={(teaserTemplate: TeaserTemplate) =>
                                dispatch(
                                    routerActions.push(
                                        `/admin-panel/master-templates/ad-board/edit/${teaserTemplate.id}`
                                    )
                                )
                            }
                        />
                    )}
                />
                <Route
                    path={'/admin-panel/master-templates/ad-board/edit/:id'}
                    render={props => (
                        <AdBoardTemplateEdit
                            {...props}
                            redirectTo={'/admin-panel/master-templates/ad-board'}
                            canEdit
                            onClose={refetch}
                        />
                    )}
                />
            </Switch>
        </div>
    );
};

export default AdBoardTemplateList;
